import type { ReactNode } from 'react';
import { characterImage, DEFAULT_CHARACTER } from '../../lib/characters.js';
import { useProfile } from '../../lib/useProfile.js';
import { BackIcon } from '../chrome/BackIcon.js';
import './page-shell.css';

/**
 * Molde "Página" do palco (estudo aprovado, "Como ficaria, com os ajustes"):
 * documento ou lista longa. O Secretário vira um selo no topo, ao lado do nome
 * da página; abaixo, o título, o número que resume e o conteúdo em linhas.
 * Fundo creme, sem banner nem personagem grande. Entra da direita (rota do palco).
 *
 * Peças de conteúdo (classes em `page-shell.css`): `pgs-figure` (número que
 * resume), `pgs-section` com `pgs-h2`, `pgs-row` (linha), `pgs-bar` (barra com
 * valor), `pgs-note` (observação do Secretário), `pgs-actions` com `pgs-btn`.
 */
export function PageShell({
  kicker,
  title,
  lead,
  onBack,
  backLabel = 'Voltar',
  aside,
  wide = false,
  hero,
  children,
}: {
  /** Nome da página ao lado do selo (ex.: "Contas"). */
  kicker: string;
  title: ReactNode;
  lead?: ReactNode;
  onBack: () => void;
  backLabel?: string;
  /** Ação curta no canto do cabeçalho (ex.: "Nova conta"). */
  aside?: ReactNode;
  /** Na mesa (1024+), a página usa a largura para duas colunas (ex.: o envio em lote). */
  wide?: boolean;
  /** Capa no lugar do título simples (ex.: a categoria do Perto de Mim); ela traz o próprio h1. */
  hero?: ReactNode;
  children: ReactNode;
}) {
  const profile = useProfile().data;
  const characterKey = profile?.characterKey ?? DEFAULT_CHARACTER;
  return (
    <div className={`pgs${wide ? ' wide' : ''}`}>
      <header className="pgs-hd">
        <button className="pgs-back back-btn" onClick={onBack} aria-label={backLabel}>
          <BackIcon />
        </button>
        <span className="pgs-seal" aria-hidden="true">
          <img src={characterImage(characterKey, 'simpatico')} alt="" />
        </span>
        <span className="pgs-kicker">{kicker}</span>
        {aside && <span className="pgs-aside">{aside}</span>}
      </header>
      {hero ?? <h1 className="pgs-h1">{title}</h1>}
      {lead && <p className="pgs-lead">{lead}</p>}
      <div className="pgs-body sk-cascade">{children}</div>
    </div>
  );
}
